import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";
import * as message from "../../../utils/constants";
import PLAN_URL from "../../../utils/PLAN_URL";

export const useCreateTableData = (
  updateStatus,
  url,
  isSnackbarUse = false
) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [updateStatus, url],
    mutationFn: async (arr) => {
      const response = await axios.post(PLAN_URL + url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: arr,
      });
      if (response.status !== 200) {
        throw new Error("Failed to add sth");
      }
      return response.status;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === updateStatus,
      });
      await queryClient.refetchQueries({
        predicate: (query) => query.queryKey[0] === updateStatus,
        type: "active",
      });
    },
    onError: (error, variables, context) => {
      if (!isSnackbarUse) {
        toast.error(message.errorCreate);
      }
    },
  });
  return mutation;
};

export const useGetTableData = (updateStatus, url, dataName = "data") => {
  const { data, ...rest } = useQuery({
    queryKey: [updateStatus, url],
    queryFn: async () => {
      const response = await axios.get(PLAN_URL + url);
      return response.data[0];
    },
    placeholderData: [],
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  return { [dataName]: data || [], ...rest };
};

export const useGetTableData2 = (updateStatus, url, dataName = "data") => {
  const { data, isLoading, isFetching, ...rest } = useQuery({
    queryKey: [updateStatus, url],
    queryFn: async () => {
      const response = await axios.get(PLAN_URL + url);
      return response.data;
    },
    placeholderData: [],
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    staleTime: 5000,
    cacheTime: 1000 * 60 * 5,
  });
  return { [dataName]: data || [], isLoading, isFetching, ...rest };
};

export const useDeleteTableData = (
  updateStatus,
  url,
  isSnackbarUse = false
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [updateStatus, url],
    mutationFn: async ({ itemId, procId, machineId }) => {
      const response = await axios.delete(
        `${PLAN_URL}${url}?itemId=${itemId}&procId=${procId}&machineId=${machineId}`
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [updateStatus],
        exact: false,
      });
      if (!isSnackbarUse) {
        toast.success(message.deleteSuccess);
      }
    },
  });
};

export const useDeleteMachineMaster = (updateStatus, url) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [updateStatus, url],
    mutationFn: async ({ machine_id, group_code, project_code }) => {
      const response = await axios.delete(
        `${PLAN_URL}${url}?machine_id=${machine_id}&group_code=${group_code}&project_code=${project_code}`
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [updateStatus],
        exact: true,
        refetchType: "all",
      });
      await queryClient.refetchQueries([updateStatus]);
      toast.success(message.deleteSuccess);
    },
    onError: (error) => {
      console.log(error);
      toast.error(
        "현재 장비를 다른 곳에서 사용중입니다. 사용 해제 후 다시 시도해 주세요."
      );
    },
  });
};

export const useDeleteTableDataPlan = (updateStatus, url) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [updateStatus, url],
    mutationFn: async ({ data, group_code, project_code }) => {
      const orderIdsString = data.join(",");
      const response = await axios.delete(
        `${PLAN_URL}${url}?data=${orderIdsString}&group_code=${group_code}&project_code=${project_code}`
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [updateStatus],
        exact: true,
        refetchType: "all",
      });
      await queryClient.refetchQueries([updateStatus]);
      toast.success(message.deleteSuccess);
    },
    onError: (error) => {
      console.error("Delete error:", error);
      toast.error(message.deleteError);
    },
  });
};

export const useDeleteTableDataPreset = (
  updateStatus,
  url,
  isSnackbarUse = false
) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [updateStatus, url],
    mutationFn: async ({ id, group_code, project_code }) => {
      const response = await axios.delete(
        `${PLAN_URL}${url}?id=${id}&group_code=${group_code}&project_code=${project_code}`
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [updateStatus],
        exact: true,
        refetchType: "all",
      });
      await queryClient.refetchQueries([updateStatus]);
      if (!isSnackbarUse) {
        toast.success(message.deleteSuccess);
      }
    },
    onError: (error) => {
      console.error("Delete error:", error);
      toast.error(message.deleteError);
    },
  });
  return mutation;
};

export const usePutTableData = (updateStatus, url, isSnackbarUse = false) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [updateStatus, url],
    mutationFn: async (arr) => {
      const response = await axios.put(PLAN_URL + url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: arr,
      });
      if (response.status !== 200) {
        throw new Error("Failed to edit sth");
      }
      return response.status;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([updateStatus]);
    },
    onError: (error, variables, context) => {
      console.log(error);
      if (error.response.data.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(message.editError);
      }
    },
  });
};

export const usePutMachineMaster = (updateStatus, url) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [updateStatus, url],
    mutationFn: async (arr) => {
      const response = await axios.put(PLAN_URL + url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: arr,
      });
      if (response.status !== 200) {
        throw new Error("Failed to edit sth");
      }
      return response.status;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([updateStatus]);
      toast.success("성공적으로 수정되었습니다.");
    },
    onError: (error, variables, context) => {
      console.log(error);
      toast.error(error.response.data.message);
    },
  });
};

export const usePutTableDataReset = (updateStatus, url) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [updateStatus, url],
    mutationFn: async (arr) => {
      const response = await axios.put(PLAN_URL + url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: arr,
      });
      if (response.status !== 200) {
        throw new Error("Failed to edit sth");
      }
      return response.status;
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([updateStatus]);
    },
    onError: (error, variables, context) => {
      console.log(error);
      toast.error(message.resetError);
    },
  });
};

export const useDeleteModelData = (updateStatus, url) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [updateStatus, url],
    mutationFn: async (modelIds) => {
      const response = await axios.delete(`${PLAN_URL}${url}`, {
        data: { model_ids: modelIds },
      });
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [updateStatus],
        exact: true,
        refetchType: "all",
      });
      await queryClient.refetchQueries([updateStatus]);
    },
    onError: (error) => {
      console.error("Delete error:", error);
      toast.error("삭제 중 오류가 발생했습니다.");
    },
  });

  return mutation;
};

export const useCreateModelData = (updateStatus, url) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [updateStatus, url],
    mutationFn: async (arr) => {
      const response = await axios.post(`${PLAN_URL}${url}`, arr, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (response.status !== 200) {
        throw new Error("Failed to add model");
      }
      const { model_id } = response.data;
      if (!model_id) {
        throw new Error("Model ID not found in response");
      }
      return model_id;
    },
    onSuccess: async (modelId) => {
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === updateStatus,
      });
      await queryClient.refetchQueries({
        predicate: (query) => query.queryKey[0] === updateStatus,
        type: "active",
      });
    },
    onError: (error) => {
      console.log(error);
      toast.error("Failed to create model. Please try again.");
    },
  });

  return mutation;
};

export const useCreateData = (
  updateStatus,
  url,
  openSnackbar,
  isHide = false
) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [updateStatus, url],
    mutationFn: async (arr) => {
      const response = await axios.post(PLAN_URL + url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: arr,
      });
      return response.data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === updateStatus,
      });
      if (!isHide) {
        openSnackbar(data.message);
      }
    },
    onError: (error) => {
      openSnackbar(
        error.response?.data?.message || "오류가 발생했습니다",
        "error"
      );
    },
  });
  return mutation;
};

export const useDeleteData = (updateStatus, url, openSnackbar) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [updateStatus, url],
    mutationFn: async ({
      itemId,
      procId,
      machineId,
      group_code,
      project_code,
    }) => {
      const response = await axios.delete(
        `${PLAN_URL}${url}?itemId=${itemId}&procId=${procId}&machineId=${machineId}&group_code=${group_code}&project_code=${project_code}`
      );
      return response.data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: [updateStatus],
        exact: false,
      });
      openSnackbar(data.message);
    },
    onError: (error) => {
      openSnackbar(
        error.response?.data?.message || "오류가 발생했습니다",
        "error"
      );
    },
  });
};

export const usePutData = (updateStatus, url, openSnackbar) => {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationKey: [updateStatus, url],
    mutationFn: async (arr) => {
      const response = await axios.put(PLAN_URL + url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: arr,
      });
      return response.data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: [updateStatus],
        exact: false,
      });
      openSnackbar(data.message);
    },
    onError: (error) => {
      openSnackbar(
        error.response?.data?.message || "오류가 발생했습니다",
        "error"
      );
    },
  });
  return mutation;
};
